'use client';

import { useState, useEffect, useCallback } from 'react';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { Trophy, TrendingUp, TrendingDown, DollarSign, Loader2, Crown, ArrowLeft } from 'lucide-react';

type GameRanking = {
  rank: number;
  userId: string;
  username: string;
  avatar: string | null;
  value: number;
  realizedPl?: number;
  unrealizedPl?: number;
  symbol?: string;
  isUnrealized?: boolean;
  isBot?: boolean;
};

type Metric = 'account_value' | 'biggest_winner' | 'biggest_loser';

function formatMoney(n: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
}

const THOTH_AVATAR   = '/assets/thoth/thoth-avatar.png';
const RA_AVATAR      = '/assets/ra/ra-avatar.png';
const OSIRIS_AVATAR  = '/assets/osiris/osiris-avatar.png';
const RAGNAR_AVATAR  = '/assets/ragnar/ragnar-avatar.png';
const CLOUD_AVATAR   = '/assets/cloud/cloud-avatar.png';
const SOLOMON_AVATAR = '/assets/solomon/solomon-avatar.webp';
const WATCHER_AVATAR = '/assets/watcher/watcher-avatar.png';

export default function GameRankingsPage() {
  const { data: session } = useSession();
  const searchParams = useSearchParams();

  const [metric, setMetric] = useState<Metric>((searchParams.get('metric') as Metric) || 'account_value');
  const [rankings, setRankings] = useState<GameRanking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seasons, setSeasons] = useState<Array<{ id: string; label: string }>>([]);
  const [seasonId, setSeasonId] = useState<string | null>(searchParams.get('seasonId'));
  const [seasonLabel, setSeasonLabel] = useState<string>('');

  const fetchRankings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = seasonId
        ? `/api/game/rankings?metric=${metric}&seasonId=${seasonId}`
        : `/api/game/rankings?metric=${metric}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setRankings(data.rankings ?? []);
        if (data.label) setSeasonLabel(data.label);
      } else {
        setError('Failed to load rankings');
        setRankings([]);
      }
    } catch {
      setError('Failed to load rankings');
      setRankings([]);
    } finally {
      setLoading(false);
    }
  }, [metric, seasonId]);

  useEffect(() => { fetchRankings(); }, [fetchRankings]);

  useEffect(() => {
    fetch('/api/game/seasons')
      .then((r) => r.ok ? r.json() : { seasons: [] })
      .then((data) => setSeasons(data.seasons ?? []))
      .catch(() => setSeasons([]));
  }, []);

  const currentUserId = (session?.user as { id?: string })?.id;

  return (
    <div className="min-h-screen text-zinc-100">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">

        {/* Header */}
        <div className="flex items-center gap-3">
          <Link
            href="/game"
            className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/80 border border-zinc-700/50 hover:border-zinc-600 transition-all"
            aria-label="Back to game"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30">
              <Trophy className="h-6 w-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Full Season Rankings</h1>
              {seasonLabel && (
                <p className="text-sm text-zinc-400 mt-0.5">Season <span className="font-mono text-amber-400/90">{seasonLabel}</span></p>
              )}
            </div>
          </div>
        </div>

        {/* Season selector */}
        {seasons.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-zinc-500">Season:</span>
            <button
              type="button"
              onClick={() => setSeasonId(null)}
              className={`px-2 py-1 rounded text-xs font-medium ${seasonId === null ? 'bg-amber-500 text-zinc-900' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
            >
              Current
            </button>
            {seasons.slice(0, 8).map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSeasonId(s.id)}
                className={`px-2 py-1 rounded text-xs font-medium ${seasonId === s.id ? 'bg-amber-500 text-zinc-900' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        {/* Metric selector */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'account_value' as const, label: 'Account value', Icon: DollarSign },
            { id: 'biggest_winner' as const, label: 'Biggest winner %', Icon: TrendingUp },
            { id: 'biggest_loser' as const, label: 'Biggest loser %', Icon: TrendingDown },
          ].map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setMetric(id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all ${
                metric === id
                  ? 'bg-amber-500 text-zinc-900 shadow-lg shadow-amber-500/25'
                  : 'bg-zinc-800/80 text-zinc-300 hover:bg-zinc-700 border border-zinc-700/50'
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {/* Rankings table */}
        <section className="rounded-2xl bg-gradient-to-br from-zinc-900/95 to-zinc-800/90 border border-zinc-700/50 p-5 sm:p-6 shadow-xl shadow-black/20">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500/80" />
              {metric === 'account_value' ? 'Account Value' : metric === 'biggest_winner' ? 'Biggest Winner %' : 'Biggest Loser %'}
            </h2>
            {!loading && rankings.length > 0 && (
              <span className="text-xs text-zinc-600">{rankings.length} player{rankings.length !== 1 ? 's' : ''}</span>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
            </div>
          ) : error ? (
            <div className="py-8 text-center">
              <p className="text-red-400/90 text-sm">{error}</p>
              <button
                type="button"
                onClick={fetchRankings}
                className="mt-2 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-zinc-900 text-sm font-medium"
              >
                Retry
              </button>
            </div>
          ) : rankings.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-8">No rankings yet for this season.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-zinc-700 text-zinc-400">
                    <th className="py-2 pr-4 w-8">#</th>
                    <th className="py-2 pr-4">Player</th>
                    {(metric === 'biggest_winner' || metric === 'biggest_loser') && (
                      <th className="py-2 pr-4">Symbol</th>
                    )}
                    <th className="py-2 pr-4 text-right">
                      {metric === 'account_value' ? 'Total value' : metric === 'biggest_winner' ? '% gain' : '% loss'}
                    </th>
                    {metric === 'account_value' && (
                      <>
                        <th className="py-2 pr-4 text-right">Realized P/L</th>
                        <th className="py-2 pr-4 text-right">Unrealized P/L</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rankings.map((r) => {
                    const isMe = currentUserId === r.userId;
                    const isThoth   = r.username === 'Thoth';
                    const isRa      = r.username === 'Ra';
                    const isOsiris  = r.username === 'Osiris';
                    const isRagnar  = r.username === 'Ragnar';
                    const isCloud   = r.username === 'Cloud';
                    const isSolomon = r.username === 'Solomon';
                    const isWatcher = r.username === 'Watcher';
                    const avatarSrc = isThoth   ? THOTH_AVATAR
                      : isRa      ? RA_AVATAR
                      : isOsiris  ? OSIRIS_AVATAR
                      : isRagnar  ? RAGNAR_AVATAR
                      : isCloud   ? CLOUD_AVATAR
                      : isSolomon ? SOLOMON_AVATAR
                      : isWatcher ? WATCHER_AVATAR
                      : (r.avatar ?? null);
                    return (
                      <tr
                        key={metric === 'account_value' ? r.userId : `${r.rank}-${r.userId}-${r.symbol ?? ''}`}
                        className={`border-b border-zinc-800 ${isMe ? 'bg-amber-500/5' : ''}`}
                      >
                        <td className="py-2 pr-4 w-8">
                          {r.rank === 1 ? (
                            <Crown className="h-4 w-4 text-yellow-400" />
                          ) : r.rank === 2 ? (
                            <span className="font-bold text-zinc-300 text-sm">2</span>
                          ) : r.rank === 3 ? (
                            <span className="font-bold text-amber-700 text-sm">3</span>
                          ) : (
                            <span className="font-mono text-zinc-600 text-sm">{r.rank}</span>
                          )}
                        </td>
                        <td className="py-2 pr-4">
                          <Link
                            href={`/profile/${r.username}`}
                            className="flex items-center gap-2 text-zinc-200 hover:text-amber-400"
                          >
                            {avatarSrc ? (
                              <Image
                                src={avatarSrc}
                                alt=""
                                width={28}
                                height={28}
                                unoptimized
                                className={`h-7 w-7 rounded-full object-cover ${r.rank <= 3 ? 'ring-1 ring-amber-500/40' : ''}`}
                              />
                            ) : (
                              <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${r.rank <= 3 ? 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/30' : 'bg-zinc-700 text-zinc-300'}`}>
                                {r.username.slice(0, 1).toUpperCase()}
                              </div>
                            )}
                            <span className={isMe ? 'text-amber-300 font-semibold' : ''}>
                              {r.username}
                            </span>
                            {isMe && (
                              <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full font-medium leading-none">You</span>
                            )}
                            {r.isBot && !isMe && (
                              <span className="text-[10px] bg-sky-500/15 text-sky-300 px-1.5 py-0.5 rounded-full font-semibold leading-none ring-1 ring-sky-500/30" title="AI trader (fixed $1,000 base, unlimited margin - ranked on realized P&L)">AI</span>
                            )}
                          </Link>
                        </td>
                        {(metric === 'biggest_winner' || metric === 'biggest_loser') && (
                          <td className="py-2 pr-4">
                            <Link href={`/chart/${r.symbol}`} target="_blank" rel="noopener noreferrer" className="font-mono text-amber-400 hover:underline">
                              {r.symbol}
                            </Link>
                          </td>
                        )}
                        <td className="py-2 pr-4 text-right font-medium">
                          {metric === 'account_value' ? (
                            <span className={r.value > 1000 ? 'text-emerald-400' : r.value < 1000 ? 'text-red-400' : 'text-zinc-300'}>
                              {formatMoney(r.value)}
                            </span>
                          ) : (
                            <span className={r.value >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                              {r.value >= 0 ? '+' : ''}{r.value.toFixed(2)}%{r.isUnrealized ? '*' : ''}
                            </span>
                          )}
                        </td>
                        {metric === 'account_value' && (
                          <>
                            <td className="py-2 pr-4 text-right">
                              <span className={(r.realizedPl ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                                {(r.realizedPl ?? 0) >= 0 ? '+' : ''}{formatMoney(r.realizedPl ?? 0)}
                              </span>
                            </td>
                            <td className="py-2 pr-4 text-right">
                              <span className={(r.unrealizedPl ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                                {(r.unrealizedPl ?? 0) >= 0 ? '+' : ''}{formatMoney(r.unrealizedPl ?? 0)}
                              </span>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(metric === 'biggest_winner' || metric === 'biggest_loser') && rankings.length > 0 && (
                <p className="text-xs text-zinc-500 mt-2">* = unrealized (open position)</p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
